import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Zap, Clock, Trophy, Play, Eye, Timer } from 'lucide-react';
import Button from '../../../components/ui/Button';

const LiveCompetitions = ({ challenges = [] }) => {
  const [liveCompetitions, setLiveCompetitions] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState({});

  useEffect(() => {
    // Mock live competitions data
    setLiveCompetitions([
      {
        id: 'live-1',
        title: 'Speed Bug Hunt Championship',
        description: 'Find as many bugs as possible in 30 minutes. Quality over quantity counts!',
        type: 'speed',
        timeLimit: 30,
        participants: 1247,
        maxParticipants: 2000,
        prizePool: 5000,
        startTime: new Date(Date.now() + 15 * 60 * 1000), // 15 minutes from now
        endTime: new Date(Date.now() + 45 * 60 * 1000),
        status: 'starting-soon',
        difficulty: 'Intermediate',
        category: 'Manual Testing',
        prizes: ['1st: 2000 points + Elite Badge', '2nd: 1500 points', '3rd: 1000 points'],
        rules: [
          'Find functional bugs in the test application',
          'Document clear reproduction steps',
          'Bugs are scored by severity and clarity',
          'No automation tools allowed'
        ]
      },
      {
        id: 'live-2',
        title: 'API Security Hackathon',
        description: 'Discover security vulnerabilities in a REST API within 90 minutes.',
        type: 'hackathon',
        timeLimit: 90,
        participants: 589,
        maxParticipants: 1000,
        prizePool: 8000,
        startTime: new Date(Date.now() + 60 * 60 * 1000), // 1 hour from now
        endTime: new Date(Date.now() + 150 * 60 * 1000),
        status: 'upcoming',
        difficulty: 'Expert',
        category: 'Security Testing',
        prizes: ['1st: 3000 points + Security Expert Badge', '2nd: 2000 points', '3rd: 1000 points'],
        rules: [
          'Test provided REST API for security vulnerabilities',
          'Use any security testing tools',
          'Document vulnerabilities with proof of concept',
          'Follow responsible disclosure practices'
        ]
      },
      {
        id: 'live-3',
        title: 'Performance Testing Marathon',
        description: 'Load test a web application and identify performance bottlenecks.',
        type: 'marathon',
        timeLimit: 120,
        participants: 342,
        maxParticipants: 500,
        prizePool: 6000,
        startTime: new Date(Date.now() + 3 * 60 * 60 * 1000), // 3 hours from now
        endTime: new Date(Date.now() + 5 * 60 * 60 * 1000),
        status: 'upcoming',
        difficulty: 'Advanced',
        category: 'Performance Testing',
        prizes: ['1st: 2500 points + Performance Pro Badge', '2nd: 1500 points', '3rd: 1000 points'],
        rules: [
          'Design and execute performance tests',
          'Identify and document bottlenecks',
          'Provide optimization recommendations',
          'Use tools like JMeter or K6'
        ]
      }
    ]);
  }, []);

  // Update countdown timers
  useEffect(() => {
    const interval = setInterval(() => {
      const newTimeRemaining = {};
      liveCompetitions?.forEach(comp => {
        const now = new Date();
        const targetTime = comp?.status === 'starting-soon' ? comp?.startTime : comp?.endTime;
        const diff = targetTime - now;
        
        if (diff > 0) {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          newTimeRemaining[comp?.id] = { hours, minutes, seconds };
        } else {
          newTimeRemaining[comp?.id] = null;
        }
      });
      setTimeRemaining(newTimeRemaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [liveCompetitions]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'bg-red-500 text-white';
      case 'starting-soon': return 'bg-orange-500 text-white';
      case 'upcoming': return 'bg-blue-500 text-white';
      case 'ended': return 'bg-gray-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'speed': return <Timer className="w-5 h-5" />;
      case 'hackathon': return <Zap className="w-5 h-5" />;
      case 'marathon': return <Trophy className="w-5 h-5" />;
      default: return <Play className="w-5 h-5" />;
    }
  };

  const handleJoinCompetition = (competitionId) => {
    console.log('Joining competition:', competitionId);
    // In a real app, this would handle competition registration
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2 flex items-center justify-center space-x-2">
          <Zap className="w-8 h-8 text-yellow-500" />
          <span>Live Competitions</span>
        </h2>
        <p className="text-muted-foreground">
          Compete in real-time against testers worldwide for prizes and glory
        </p>
      </div>
      {/* Competition Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-red-100 to-red-200 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-red-700 font-medium">Live Now</span>
          </div>
          <div className="text-2xl font-bold text-red-700">
            {liveCompetitions?.filter(c => c?.status === 'live')?.length}
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg p-4 text-center">
          <div className="text-orange-600 font-medium mb-2">Starting Soon</div>
          <div className="text-2xl font-bold text-orange-700">
            {liveCompetitions?.filter(c => c?.status === 'starting-soon')?.length}
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg p-4 text-center">
          <div className="text-blue-600 font-medium mb-2">Upcoming</div>
          <div className="text-2xl font-bold text-blue-700">
            {liveCompetitions?.filter(c => c?.status === 'upcoming')?.length}
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-lg p-4 text-center">
          <div className="text-green-600 font-medium mb-2">Total Prize Pool</div>
          <div className="text-2xl font-bold text-green-700">
            ${liveCompetitions?.reduce((sum, c) => sum + c?.prizePool, 0)?.toLocaleString()}
          </div>
        </div>
      </div>
      {/* Live Competitions List */}
      <div className="space-y-6">
        {liveCompetitions?.map((competition, index) => (
          <motion.div
            key={competition?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-card border border-border rounded-xl p-6 relative overflow-hidden"
          >
            {/* Status Badge */}
            <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(competition?.status)}`}>
              {competition?.status === 'starting-soon' && 'Starting Soon'}
              {competition?.status === 'live' && 'LIVE NOW'}
              {competition?.status === 'upcoming' && 'Upcoming'}
            </div>

            {/* Competition Header */}
            <div className="flex items-start space-x-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white">
                {getTypeIcon(competition?.type)}
              </div>
              
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">{competition?.title}</h3>
                <p className="text-muted-foreground mb-3">{competition?.description}</p>
                
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="bg-muted px-2 py-1 rounded">{competition?.category}</span>
                  <span className="bg-muted px-2 py-1 rounded">{competition?.difficulty}</span>
                  <span className="text-muted-foreground">{competition?.timeLimit} minutes</span>
                </div>
              </div>
            </div>

            {/* Countdown Timer */}
            {timeRemaining?.[competition?.id] && (
              <div className="bg-gradient-to-r from-orange-100 to-red-100 rounded-lg p-4 mb-4">
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-2">
                    {competition?.status === 'starting-soon' ? 'Starts in:' : 'Ends in:'}
                  </div>
                  <div className="flex items-center justify-center space-x-4 text-2xl font-bold">
                    <div className="text-center">
                      <div>{timeRemaining?.[competition?.id]?.hours?.toString()?.padStart(2, '0')}</div>
                      <div className="text-xs text-muted-foreground">Hours</div>
                    </div>
                    <div>:</div>
                    <div className="text-center">
                      <div>{timeRemaining?.[competition?.id]?.minutes?.toString()?.padStart(2, '0')}</div>
                      <div className="text-xs text-muted-foreground">Minutes</div>
                    </div>
                    <div>:</div>
                    <div className="text-center">
                      <div>{timeRemaining?.[competition?.id]?.seconds?.toString()?.padStart(2, '0')}</div>
                      <div className="text-xs text-muted-foreground">Seconds</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Competition Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Participants */}
              <div>
                <h4 className="font-medium mb-3">Participation</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Registered</span>
                    <span className="font-medium">
                      {competition?.participants?.toLocaleString()} / {competition?.maxParticipants?.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(competition?.participants / competition?.maxParticipants) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Prizes */}
              <div>
                <h4 className="font-medium mb-3">Prizes</h4>
                <div className="space-y-1">
                  {competition?.prizes?.map((prize, idx) => (
                    <div key={idx} className="text-sm text-muted-foreground">
                      {prize}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Rules */}
            <div className="mb-6">
              <h4 className="font-medium mb-3">Rules</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {competition?.rules?.map((rule, idx) => (
                  <div key={idx} className="text-sm text-muted-foreground flex items-start space-x-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>{rule}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Trophy className="w-4 h-4" />
                  <span>${competition?.prizePool?.toLocaleString()} prize pool</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{competition?.timeLimit} min duration</span>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Button variant="outline" size="sm">
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </Button>
                <Button 
                  size="sm"
                  onClick={() => handleJoinCompetition(competition?.id)}
                  disabled={competition?.status === 'ended'}
                >
                  {competition?.status === 'live' ? 'Join Now' : 'Register'}
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {/* Empty State */}
      {liveCompetitions?.length === 0 && (
        <div className="text-center py-12">
          <Zap className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No live competitions right now</h3>
          <p className="text-muted-foreground mb-4">
            Check back soon for exciting competitions and challenges
          </p>
          <Button variant="outline">
            Set Notification Reminder
          </Button>
        </div>
      )}
      {/* Competition Guidelines */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Competition Guidelines</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="font-medium mb-2">Fair Play</h4>
            <ul className="space-y-1 text-muted-foreground">
              <li>• No cheating or collaboration unless specified</li>
              <li>• Use only allowed tools and resources</li>
              <li>• Submit original work only</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Scoring</h4>
            <ul className="space-y-1 text-muted-foreground">
              <li>• Quality is valued over quantity</li>
              <li>• Clear documentation required</li>
              <li>• Bonus points for innovative approaches</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default LiveCompetitions;